import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../records/grids/columns/columnDefinitionService";
import {
  ColumnDefinitionSetService,
  ColumnDefinitionSetServiceProvider,
  vsProviderName as ColumnDefinitionSetServiceProviderName,
  vsServiceName as ColumnDefinitionSetServiceName,
} from "../../../records/grids/columns/columnDefinitionSetService";
import {
  GridConfiguration,
  RecordGridViewService,
  RecordGridViewServiceProvider,
  vsProviderName as RecordGridViewServiceProviderName,
  vsServiceName as RecordGridViewServiceName,
} from "../../../records/views/recordGridView";
import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
} from "../../../records/grids/settings/gridSettingsService";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../records/server/serverUtility";

import { RecordDataSource } from "../../../records/grids";

import { vsObjectName as machineTypeIdObjectName } from "./machineConfigurationType";
import { vsObjectName as themeName } from "./lookup/theme";
import { vsObjectName as gameCategoryName } from "./lookup/gameCategory";
import {
  FormViewConfig,
  FormViewService,
  FormViewServiceProvider,
  vsProviderName as FormViewServiceProviderName,
  vsServiceName as FormViewServiceName,
} from "../../../records/forms/formView";
import { StateService } from "@uirouter/angularjs";
import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../records/forms/formly/fieldConfigurationHelpers";
import fieldHelpers, { columnLayout } from "../../../ui-formly/fieldHelpers";
import { LookupManagementService } from "../../../records/lookup/lookupManagement";
import { vsLookupManagementServiceName as MachineLookupManagementServiceName } from "../../shared/lookups";
import { denominationName } from "./lookup";
import { percentageValueGetterFactory } from "../../../records/grids/cells/valueGetters";

export const vsObjectName = "MachineConfigurationTypeGameConfiguration";

machineGameConfig.$inject = [
  RecordGridViewServiceProviderName,
  ColumnDefinitionSetServiceProviderName,
  FormViewServiceProviderName,
];
export function machineGameConfig(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider,
  formViewServiceProvider: FormViewServiceProvider
) {
  recordGridViewServiceProvider.addGridConfiguration(vsObjectName, [
    ServerUtilityName,
    GridSettingsServiceName,
    RecordGridViewServiceName,
    (
      serverUtility: ServerUtility,
      gridSettingsService: GridSettingsService,
      recordGridViewService: RecordGridViewService
    ): GridConfiguration => {
      const machineTypeIdGridConfig =
        recordGridViewService.getGridConfiguration(machineTypeIdObjectName);
      return {
        columnDefinitionSet: vsObjectName,
        defaultSort: RecordGridViewService.combineSort(
          machineTypeIdGridConfig?.defaultSort,
          "Order"
        ),
        gridDataSource: new RecordDataSource(
          serverUtility,
          "/api/machines/MachineConfigurationTypeGameConfiguration"
        ),
        gridSettings: gridSettingsService.getGridSettings(Symbol(vsObjectName)),
        namePlural: "Machine Game Configurations",
        nameSingular: "Machine Game Configuration",
        showAddButton: false,
      };
    },
  ]);

  columnDefinitionSetServiceProvider.addColumnDefinitionSet(vsObjectName, [
    ColumnDefinitionServiceName,
    ColumnDefinitionSetServiceName,
    (
      columnDefinitionService: ColumnDefinitionService,
      columnDefinitionSetService: ColumnDefinitionSetService
    ) => {
      const cols = [
        columnDefinitionService.getNumberColDef({
          field: "Id",
          hide: true,
        }),
        columnDefinitionService.getStringColDef({
          field: "MachineThemeName",
          header: "Theme",
        }),
        columnDefinitionService.getStringColDef({
          field: "MachineDenominationName",
          header: "Denomination",
        }),
        columnDefinitionService.getStringColDef({
          field: "SeriesFamily",
          header: "Series/Family",
        }),
        columnDefinitionService.getStringColDef({
          field: "Paylines",
          header: "Paylines",
        }),
        columnDefinitionService.getStringColDef({
          field: "PaytableId",
          header: "Paytable ID",
        }),
        columnDefinitionService.getStringColDef({
          field: "Volatility",
          header: "Volatility",
        }),
        columnDefinitionService.getStringColDef({
          field: "MachineGameCategoryName",
          header: "Game Category",
        }),
        columnDefinitionService.getNumberColDef({
          field: "PopPercentage",
          header: "Pop %",
          valueGetter: percentageValueGetterFactory(),
        }),
        columnDefinitionService.getNumberColDef({
          field: "ProgressivePercentage",
          header: "Prog %",
          valueGetter: percentageValueGetterFactory(),
        }),
        columnDefinitionService.getNumberColDef({
          field: "HoldPercentage",
          header: "Hold %",
          valueGetter: percentageValueGetterFactory(),
        }),
        columnDefinitionService.getNumberColDef({
          field: "ParPercentage",
          header: "Par %",
          valueGetter: percentageValueGetterFactory(),
        }),
        columnDefinitionService.getNumberColDef({
          field: "OptPercentage",
          header: "OPT%",
          valueGetter: percentageValueGetterFactory(),
        }),
        columnDefinitionService.getNumberColDef({
          field: "DevParPercentage",
          header: "Dev Par%",
          valueGetter: percentageValueGetterFactory(),
        }),
        columnDefinitionService.getNumberColDef({
          field: "MinBetDollar",
          header: "Min Bet ($)",
        }),
        columnDefinitionService.getNumberColDef({
          field: "MaxBetDollar",
          header: "Max Bet ($)",
        }),
        columnDefinitionService.getNumberColDef({
          field: "MaxCoins",
          header: "Max Coins",
        }),
        columnDefinitionService.getNumberColDef({
          field: "ApproximateMaxLiability",
          header: "~ Max Liability",
        }),
        columnDefinitionService.getStringColDef({
          field: "MachineProgressiveGameName",
          header: "Progressive Levels",
        }),
        ...columnDefinitionSetService.getColumnDefinitionSet(
          machineTypeIdObjectName
        ),
      ];

      return cols;
    },
  ]);

  formViewServiceProvider.addFormViewConfig(vsObjectName, [
    FormViewServiceName,
    "$state",
    FieldConfigurationHelperServiceName,
    MachineLookupManagementServiceName,
    (
      formViewService: FormViewService,
      $state: StateService,
      fieldConfigurationHelperService: FieldConfigurationHelperService,
      machineLookupManagementService: LookupManagementService
    ): FormViewConfig => {
      return {
        fields: [
          fieldConfigurationHelperService.repeaterField({
            key: "GameConfigs",
            allowAdd: true,
            allowAddBottom: true,
            displayNameSingular: "Game Configuration",
            displayNamePlural: "Game Configurations",
            showHeader: true,
            afterAdd(gameConfig, formModel) {
              //do this so progressives level drop down can get MachineConfigurationTypeId
              gameConfig.MachineConfigurationTypeId = formModel.Id;
            },
            repeaterFields: [
              {
                className: "row",
                fieldGroup: [
                  fieldConfigurationHelperService.lookupField(
                    machineLookupManagementService,
                    {
                      key: "MachineThemeId",
                      required: true,
                      label: "Theme",
                      objectName: themeName,
                      className: "col-xs-6",
                    }
                  ),
                  fieldConfigurationHelperService.lookupField(
                    machineLookupManagementService,
                    {
                      key: "MachineDenominationId",
                      required: true,
                      label: "Denomination",
                      objectName: denominationName,
                      className: "col-xs-3",
                    }
                  ),
                ],
              },
              ...fieldHelpers.columnLayout(
                [
                  fieldHelpers.simpleStringField({
                    key: "SeriesFamily",
                    label: "Series/Family",
                  }),
                  fieldHelpers.simpleStringField({
                    key: "Paylines",
                  }),
                  fieldHelpers.simpleStringField({
                    key: "PaytableId",
                    label: "Paytable ID",
                  }),
                  fieldHelpers.simpleStringField({
                    key: "Volatility",
                  }),
                  fieldConfigurationHelperService.lookupField(
                    machineLookupManagementService,
                    {
                      objectName: gameCategoryName,
                      key: "MachineGameCategoryId",
                      label: "Game Category",
                    }
                  ),
                  fieldConfigurationHelperService.selectAsyncField({
                    key: "MachineProgressiveGameId",
                    label: "Progressive Levels",
                    queryUrl:
                      "/api/machines/MachineConfigurationType/progressiveGames",
                    labelProp: "MachineProgressiveGameName",
                    getDefaultItem: (model: any) => {
                      return {
                        ...model.MachineProgressiveGame,
                        MachineProgressiveGameName:
                          model?.MachineProgressiveGame?.Name,
                      };
                    },
                    queryBuilder: (search, model) => {
                      return {
                        MachineProgressiveGameNameContains: search,
                        MachineConfigurationTypeId:
                          model.MachineConfigurationTypeId,
                      };
                    },
                  }),
                ],
                4
              ),
              ...fieldHelpers.columnLayout(
                [
                  fieldHelpers.simplePercentageField({
                    key: "PopPercentage",
                    label: "Pop %",
                  }),
                  fieldHelpers.simplePercentageField({
                    key: "ProgressivePercentage",
                    label: "Prog  %",
                  }),
                  fieldHelpers.simplePercentageField({
                    key: "HoldPercentage",
                    label: "Hold %",
                  }),
                  fieldHelpers.simplePercentageField({
                    key: "ParPercentage",
                    label: "Par %",
                  }),
                  fieldHelpers.simplePercentageField({
                    key: "OptPercentage",
                    label: "OPT%",
                  }),
                  fieldHelpers.simplePercentageField({
                    key: "DevParPercentage",
                    label: "Dev Par%",
                  }),
                ],
                4
              ),
              ...fieldHelpers.columnLayout(
                [
                  fieldHelpers.simpleCurrencyField({
                    key: "MinBetDollar",
                    label: "Min Bet ($)",
                  }),
                  fieldHelpers.simpleCurrencyField({
                    key: "MaxBetDollar",
                    label: "Max Bet ($)",
                  }),
                  fieldHelpers.simpleNumberField({
                    key: "MaxCoins",
                    label: "Max Coins",
                  }),
                  fieldHelpers.simpleStringField({
                    key: "ApproximateMaxLiability",
                    label: "~ Max Liability",
                  }),
                  {
                    key: "copyDelete",
                    type: "recordRepeaterCopyDelete",
                    templateOptions: {
                      label: " ",
                      allowMove: true,
                    },
                  },
                ],
                4
              ),
            ],
          }),
        ],
        name: vsObjectName,
        title: () => "Edit Game Configurations",
        submitForm: (args) =>
          formViewService
            .submitForm({
              formObject: { Children: (<any>args.model).GameConfigs ?? [] },
              formViewController: args.formViewController,
              method: "PUT",
              url: `/api/machines/MachineConfigurationType/${
                (<any>args.model)?.Id
              }/gameConfigs`,
            })
            .then(() => $state.go("^", undefined, { reload: true })),
      };
    },
  ]);
}
